import React, { useMemo } from 'react'
import { Box, Table as MuiTable, TableBody, TableCell, TableContainer, TableRow, TableHead, Typography, IconButton, Select, MenuItem, TextField, Stack, Button, Divider } from "@mui/material"
import { flexRender } from '@tanstack/react-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAnglesLeft, faAnglesRight, faChevronLeft, faChevronRight, faEllipsis, faRefresh } from '@fortawesome/pro-regular-svg-icons'

export const Table = ({
  table, hover, onRowClick, noData = 'No records',
  onRefresh, loading,
  renderExpandedRow = () => <></>,
  enableColumnOrdering = false,
  expandedRowCellSx,
  ...props
}) => {

  const handleRowClick = row => {
    if (row.getCanSelect())
      row.toggleSelected()
    if (!!onRowClick)
      onRowClick(row)
  }

  // Lets users drag column headers to rearrange the table to their preferred view.
  const handleColumnDragStart = (e, columnId) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', columnId)
  }
  const handleColumnDragOver = e => e.preventDefault()
  const handleColumnDrop = (e, targetColumnId) => {
    e.preventDefault()
    const draggedColumnId = e.dataTransfer.getData('text/plain')
    if (!draggedColumnId || draggedColumnId === targetColumnId) return

    const currentOrder = table.getState().columnOrder?.length
      ? table.getState().columnOrder
      : table.getAllLeafColumns().map(column => column.id)
    const nextOrder = [...currentOrder]
    const fromIndex = nextOrder.indexOf(draggedColumnId)
    const toIndex = nextOrder.indexOf(targetColumnId)
    if (fromIndex === -1 || toIndex === -1) return

    nextOrder.splice(fromIndex, 1)
    nextOrder.splice(toIndex, 0, draggedColumnId)
    table.setColumnOrder(nextOrder)
  }

  const isFiltered = useMemo(() => {
    return table.getPreFilteredRowModel().rows.length
      !== table.getRowModel().rows.length
  }, [table])

  return <>
    <TableContainer>
      <MuiTable {...props}>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup, groupDepth, groups) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers
              .filter(header => !header.column.columnDef.meta?.hidden)
              .map((header) => {
                if (header.column.depth !== groupDepth)
                  return null
                const canReorder = enableColumnOrdering && header.column.id !== 'expand'
                return <TableCell
                  key={header.id}
                  draggable={canReorder}
                  onDragStart={canReorder ? (e) => handleColumnDragStart(e, header.column.id) : undefined}
                  onDragOver={canReorder ? handleColumnDragOver : undefined}
                  onDrop={canReorder ? (e) => handleColumnDrop(e, header.column.id) : undefined}
                  sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    cursor: canReorder
                      ? 'grab'
                      : header.column.getCanSort() ? 'pointer' : 'default',
                    userSelect: 'none',
                    '&:hover': {
                      backgroundColor: header.column.getCanSort()
                        ? 'action.hover'
                        : 'transparent',
                    },
                    width: header.getSize()
                  }}
                  colSpan={header.colSpan}
                  rowSpan={header.isPlaceholder
                    ? groups.length : 1}
                  onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : null}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() && (
                      <Box sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                        {header.column.getIsSorted() === 'asc' ? '↑' : '↓'}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              })}
            </TableRow>
          ))}
        </TableHead>

        <TableBody>
          {loading
            ? <TableRow>
              <TableCell colSpan={table.getVisibleLeafColumns().length} align="center">
                <Typography variant="body2" color='info'>
                  Fetching rows&nbsp;
                  <FontAwesomeIcon icon={faEllipsis}
                    fade />
                </Typography>
              </TableCell>
            </TableRow> : <>
              {table.getRowModel().rows.map((row) => (<React.Fragment key={row.id}>
                <TableRow
                  hover={hover}
                  onClick={() => handleRowClick(row)}
                  sx={{ cursor: !onRowClick ? 'default' : 'pointer' }}
                >
                  {row.getVisibleCells()
                  .filter(cell => !cell.column.columnDef.meta?.hidden)
                  .map((cell, c) => (
                    <TableCell key={cell.id || c}
                      sx={[
                        row.getIsExpanded()
                        && { borderBottom: 0 },
                        !cell.column.columnDef.accessorKey
                        && { p: 1, [c === 0 && 'pr']: 0 }
                      ]}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
                {row.getIsExpanded() && (
                  <TableRow>
                    <TableCell colSpan={table.getVisibleLeafColumns().length} sx={expandedRowCellSx}>
                      {renderExpandedRow(row)}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>))}
              {!table.getRowModel().rows?.length && (
                <TableRow>
                  <TableCell colSpan={table.getVisibleLeafColumns().length} align="center">
                    {!noData || typeof noData === 'string'
                      ? <Typography variant="body2" color='info'>{noData}</Typography>
                      : noData
                    }
                  </TableCell>
                </TableRow>
              )}
            </>}
        </TableBody>
      </MuiTable>
    </TableContainer>

    { /* Controls */}
    <Divider />
    <Stack
      gap={1}
      p={2}
      alignItems="center"
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Button variant="contained" color="primary"
          startIcon={
            <FontAwesomeIcon icon={faRefresh}
              style={{ width: 16, height: 16 }}
              spin={loading}
            />
          }
          onClick={onRefresh}
        >
          Refresh data
        </Button>
        <Typography variant="body1">
          {table.getRowModel().rows.length}
          {isFiltered && <> of {table.getPreFilteredRowModel().rows.length}</>} rows

        </Typography>
      </Box>

      { /* Pagination */}
      <Box
        sx={{
          display: {
            xs: 'block',
            sm: 'flex',
          },
        }}
        alignItems="center"
        gap={1}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="body1">Page</Typography>
          <Typography variant="body1" fontWeight={600}>
            {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          | Go to page:
                    <TextField
            type="number"
            inputProps={{ min: 1, max: table.getPageCount() }}
            defaultValue={table.getState().pagination.pageIndex + 1}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (!val || val < 1) return;
              table.setPageIndex(Math.min(val, table.getPageCount()) - 1);
            }}
            size="small" sx={{ mb: '-5px' }}
          />
        </Stack>
        <Select
          value={table.getState().pagination.pageSize}
          onChange={(e) => {
            table.setPageSize(Number(e.target.value));
          }}
          size="small" sx={{ mb: '-5px' }}
        >
          {[5, 10, 15, 20, 25].map((pageSize) => (
            <MenuItem key={pageSize} value={pageSize}>
              {pageSize}
            </MenuItem>
          ))}
        </Select>

        <IconButton
          size="small"
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
        >
          <FontAwesomeIcon icon={faAnglesLeft} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          disabled={!table.getCanNextPage()}
        >
          <FontAwesomeIcon icon={faAnglesRight}
            spin={loading}
          />
        </IconButton>
      </Box>
    </Stack>
  </>
}